import React, { createContext, useContext, useState, useEffect } from 'react';
import { User, Session } from '@supabase/supabase-js';
import { supabase } from '@/integrations/supabase/client';
import { Log } from '@/utils/logger';

interface AuthContextType {
  user: User | null;
  session: Session | null;
  login: (email: string, password: string) => Promise<{ error: any }>;
  signUp: (email: string, password: string, firstName?: string, lastName?: string, additionalData?: { country_code: string; contact_number: string; company_name: string }) => Promise<{ error: any }>;
  loginWithGoogle: () => Promise<{ error: any }>;
  logout: () => Promise<void>;
  resetPassword: (email: string) => Promise<{ error: any }>;
  updatePassword: (password: string) => Promise<{ error: any }>;
  isLoading: boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    // Set up auth state listener
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      async (event, session) => {
        setSession(session);
        setUser(session?.user ?? null);
        setIsLoading(false);

        // Trigger country detection for new signups and Free tier logins
        if (session?.user && (event === 'SIGNED_IN' || event === 'TOKEN_REFRESHED')) {
          // Add a delay to ensure session is fully established
          setTimeout(async () => {
            try {
              // Verify session is still valid before proceeding
              const { data: { session: currentSession } } = await supabase.auth.getSession();
              if (!currentSession?.user) {
                Log.action('No valid session - skipping country detection');
                return;
              }

              // Check user tier first
              const { data: profile } = await supabase
                .from('profiles')
                .select('user_tier')
                .eq('id', currentSession.user.id)
                .single();

              // Only detect country for Free tier users or new signups
              if (!profile || profile.user_tier === 'Free') {
                console.log('Initiating country detection');
                const { error } = await supabase.functions.invoke('detect-user-country');
                if (error) {
                  console.error('Country detection function error:', error);
                } else {
                  console.log('Country detection completed successfully');
                }
              }
            } catch (error) {
              console.error('Country detection error:', error);
            }
          }, 1000); // Increased delay to 1 second
        }
      }
    );

    // Get initial session
    supabase.auth.getSession().then(({ data: { session } }) => {
      setSession(session);
      setUser(session?.user ?? null);
      setIsLoading(false);
    });

    return () => subscription.unsubscribe();
  }, []);

  const signUp = async (email: string, password: string, firstName?: string, lastName?: string, additionalData?: { country_code: string; contact_number: string; company_name: string }) => {
    setIsLoading(true);
    const redirectUrl = `${window.location.origin}/`;
    
    const userData: Record<string, any> = {};
    
    if (firstName) userData.first_name = firstName;
    if (lastName) userData.last_name = lastName;
    if (additionalData) {
      userData.country_code = additionalData.country_code;
      userData.contact_number = additionalData.contact_number;
      userData.company_name = additionalData.company_name;
    }
    
    const { error } = await supabase.auth.signUp({
      email,
      password,
      options: {
        emailRedirectTo: redirectUrl,
        data: Object.keys(userData).length > 0 ? userData : undefined
      }
    });
    
    setIsLoading(false);
    return { error };
  };

  const login = async (email: string, password: string) => {
    setIsLoading(true);
    const { error } = await supabase.auth.signInWithPassword({
      email,
      password,
    });
    setIsLoading(false);
    return { error };
  };

  const loginWithGoogle = async () => {
    setIsLoading(true);
    
    const { error } = await supabase.auth.signInWithOAuth({
      provider: 'google',
      options: {
        redirectTo: `${window.location.origin}/`,
        scopes: 'openid email profile'
      }
    });
    setIsLoading(false);
    
    return { error };
  };

  const logout = async () => {
    setIsLoading(true);
    try {
      const { error } = await supabase.auth.signOut();
      if (error) {
        Log.error('Logout failed', { error: error.message });
        throw error;
      }
      // Clear local state immediately
      setSession(null);
      setUser(null);
    } catch (error: any) {
      Log.error('Logout error', { error: error.message });
      // Even if logout fails, clear local state
      setSession(null);
      setUser(null);
    } finally {
      setIsLoading(false);
    }
  };

  const resetPassword = async (email: string) => {
    setIsLoading(true);
    const { error } = await supabase.auth.resetPasswordForEmail(email, {
      redirectTo: `${window.location.origin}/auth?reset=true`,
    });
    setIsLoading(false);
    return { error };
  };

  const updatePassword = async (password: string) => {
    setIsLoading(true);
    const { error } = await supabase.auth.updateUser({
      password: password
    });
    setIsLoading(false);
    return { error };
  };

  return (
    <AuthContext.Provider value={{ 
      user, 
      session, 
      login, 
      signUp, 
      loginWithGoogle, 
      logout, 
      resetPassword,
      updatePassword,
      isLoading 
    }}>
      {children}
    </AuthContext.Provider>
  );
};
